import { useGraphMutation, useToast } from "@dashboard/library";
import { useTranslation } from "react-i18next";

import { UseLinkTagsToCallArgs } from "./useLinkTagsToCall.decl";

import { tagFragment } from "@/graphql/fragments/Tag";
import { LinkTagsToCall } from "@/graphql/mutations/LinkTagsToCall";
import { GetTagsAndNotes } from "@/graphql/queries/GetTagsAndNotes";
import { useBaseStore } from "@/store/store";

export function useLinkTagsToCall({ callId }: UseLinkTagsToCallArgs) {
  const { t } = useTranslation();
  const { show } = useToast();

  const setMutationLoading = useBaseStore((state) => state.setMutationLoading);

  const [linkTagsToCall, { loading }] = useGraphMutation(LinkTagsToCall, {
    onCompleted() {
      show({
        message: t("tag_saved"),
        variant: "success",
      });
      setMutationLoading(false);
    },
    onError(e) {
      show({
        variant: "critical",
        message: e.message,
      });
      setMutationLoading(false);
    },
  });

  const updateTags = (tagIds: string[]): void => {
    setMutationLoading(true);

    const mappedTagIds = tagIds.map(Number);

    // The REST API expects [0] in the payload when you want to clear all tags.
    if (!tagIds.length) {
      linkTagsToCall({
        variables: {
          input: {
            callId,
            tagIds: [0],
          },
        },
        optimisticResponse: {
          linkTagsToCall: [],
        },
        update(cache) {
          const readTagsAndNotes = cache.readQuery({
            query: GetTagsAndNotes,
            variables: {
              callId,
            },
          });

          cache.writeQuery({
            query: GetTagsAndNotes,
            data: {
              getTagsAndNotes: {
                __typename: "TagsAndNotes",
                notes: readTagsAndNotes?.getTagsAndNotes?.notes ?? [],
                tags: [],
              },
            },
            variables: {
              callId,
            },
          });
        },
      });
    } else {
      linkTagsToCall({
        variables: {
          input: {
            callId,
            tagIds: mappedTagIds,
          },
        },
        optimisticResponse: {
          linkTagsToCall: tagIds.map((id) => ({
            __typename: "Tag",
            id,
          })),
        },
        update(cache) {
          const readTagsAndNotes = cache.readQuery({
            query: GetTagsAndNotes,
            variables: {
              callId,
            },
          });

          cache.writeQuery({
            query: GetTagsAndNotes,
            data: {
              getTagsAndNotes: {
                __typename: "TagsAndNotes",
                notes: readTagsAndNotes?.getTagsAndNotes?.notes ?? [],
                tags: tagIds.map((id) =>
                  cache.readFragment({
                    id: cache.identify({ __typename: "Tag", id }),
                    fragment: tagFragment,
                  })
                ),
              },
            },
            variables: {
              callId,
            },
          });
        },
      });
    }
  };

  return {
    updateTags,
    loading,
  };
}
